import type { Campaign } from '../types'
import { hasCampaignEndDatePassed } from './campaign-end-date'
import { campaignDisplayName } from './campaign-list-display'
import { campaignEditHref, isCampaignLaunched } from './campaignResumeWizardPath'
export type CampaignLandingFilter = 'all' | 'active' | 'draft' | 'completed'

export type CampaignLandingCardStatus = 'active' | 'draft' | 'completed'

export const CAMPAIGN_LANDING_FILTER_OPTIONS: ReadonlyArray<{
  value: CampaignLandingFilter
  label: string
}> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
  { value: 'completed', label: 'Completed' },
]

export function resolveLandingCardStatus(campaign: Campaign): CampaignLandingCardStatus {
  if (isCampaignCompleted(campaign)) return 'completed'
  if (isCampaignLaunched(campaign)) return 'active'
  return 'draft'
}

/** Completed when stored as completed, or launched and every instrument is closed, or the campaign end date has passed. */
export function isCampaignCompleted(campaign: Campaign): boolean {
  if (campaign.status === 'completed') return true
  if (!isCampaignLaunched(campaign)) return false
  if (campaign.allInstrumentsCompleted === true) return true
  return hasCampaignEndDatePassed(campaign.endDate)
}
export function matchesLandingFilter(
  campaign: Campaign,
  filter: CampaignLandingFilter,
): boolean {
  if (filter === 'all') return true
  return resolveLandingCardStatus(campaign) === filter
}

/** Matches Recent Campaigns “Active” pill — same rules as the admin dashboard table filter. */
export function countActiveLandingCampaigns(campaigns: ReadonlyArray<Campaign>): number {
  return campaigns.filter((campaign) => resolveLandingCardStatus(campaign) === 'active').length
}

export function matchesCampaignLandingSearch(campaign: Campaign, query: string): boolean {
  const trimmed = query.trim()
  if (!trimmed) return true
  return campaignCardTitle(campaign).toLowerCase().includes(trimmed.toLowerCase())
}

export function sortCampaignsByUpdatedDesc(campaigns: Campaign[]): Campaign[] {
  return [...campaigns].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

export function sortCampaignsByName(
  campaigns: Campaign[],
  direction: 'asc' | 'desc',
): Campaign[] {
  const factor = direction === 'asc' ? 1 : -1
  return [...campaigns].sort((a, b) => {
    const cmp = campaignCardTitle(a).localeCompare(campaignCardTitle(b), undefined, {
      sensitivity: 'base',
    })
    return cmp * factor
  })
}

export function formatCampaignCreatedDate(iso: string): string {
  const date = new Date(iso)
  if (!Number.isFinite(date.getTime())) return '—'
  const month = date.getMonth() + 1
  const day = date.getDate()
  const year = date.getFullYear() % 100
  return `${month}/${day}/${year}`
}

export function formatBudgetUsed(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '—'
  // Dollar sign is shown via CampaignBudgetUsedIcon on the card — omit `$` here.
  return amount.toLocaleString('en-US', {
    maximumFractionDigits: 0,
  })
}

export function campaignCardMetrics(campaign: Campaign): {
  loiCount: number
  instrumentCount: number
  totalResponses: number
} {
  const loiCount = Array.isArray(campaign.linesOfInquiry) ? campaign.linesOfInquiry.length : 0
  return {
    loiCount,
    instrumentCount: campaign.instrumentCount ?? 0,
    totalResponses: campaign.totalInstrumentResponses ?? 0,
  }
}

export function campaignCardTitle(campaign: Campaign): string {
  return campaignDisplayName(campaign.name)
}

/** Primary navigation when the user clicks the card body (TC-CLP-18 / TC-CLP-20). */
export function campaignCardPrimaryHref(campaign: Campaign): string {
  if (campaign.companyStatus === 'inactive') {
    return `/campaigns/${campaign.id}`
  }
  if (resolveLandingCardStatus(campaign) === 'draft') {
    return campaignEditHref(campaign)
  }
  return `/campaigns/${campaign.id}`
}

export const LANDING_STATUS_PILL: Record<
  CampaignLandingCardStatus,
  { label: string; bg: string; border: string; color: string }
> = {
  active: {
    label: 'Active',
    bg: '#d1fae5',
    border: '#10b981',
    color: '#065f46',
  },
  completed: {
    label: 'Completed',
    bg: '#e3f2fd',
    border: '#3285d7',
    color: '#3b82f6',
  },
  draft: {
    label: 'Draft',
    bg: '#fef3c7',
    border: '#f59e0b',
    color: '#92400e',
  },
}
